/**
 * Rating calibration: the expected distribution, and the variance from it.
 *
 * The grade bands in `rating.ts` answer "what grade is this score?". This file
 * answers a different question: "how many people should hold each grade?".
 *
 * The two are deliberately independent. A band is a property of one person's
 * score; a target is a property of the population. Managers rate against the
 * bands with no quota at all - the targets apply once, org-wide, at HR's
 * calibration panel, because a manager with four reports cannot hold a 5% A+
 * quota without rounding deciding somebody's year.
 *
 * A target is a GUIDELINE the panel is measured against, never a rule that moves
 * anybody automatically. Nothing here mutates a grade; `bandVariance` reports the
 * gap and a human decides what to do about it.
 */

import { GRADES, type Grade } from './rating';

export interface GradeTarget {
  grade: Grade;
  /** Whole percent of the population expected to hold this grade. */
  targetPct: number;
}

export const TARGET_TOTAL_PCT = 100;

/**
 * The organization's starting curve, seeded per cycle and editable by HR.
 *
 * The brief named five buckets - A+ 5, A 15, B 70, C 5, D 5 - against a scale
 * that persists six. B's 70 is split across B+ and B rather than dropping B+,
 * which would mean migrating a persisted enum and rebanding every finalGrade
 * already recorded.
 */
export const DEFAULT_GRADE_TARGETS: ReadonlyArray<GradeTarget> = [
  { grade: 'A+', targetPct: 5 },
  { grade: 'A', targetPct: 15 },
  { grade: 'B+', targetPct: 20 },
  { grade: 'B', targetPct: 50 },
  { grade: 'C', targetPct: 5 },
  { grade: 'D', targetPct: 5 },
];

export type TargetIssueCode =
  | 'NOT_INTEGER'
  | 'OUT_OF_RANGE'
  | 'MISSING_GRADE'
  | 'DUPLICATE_GRADE'
  | 'TOTAL_MISMATCH';

export interface TargetIssue {
  code: TargetIssueCode;
  message: string;
  /** The grade at fault, or null for whole-set issues. */
  grade: Grade | null;
}

export interface TargetValidation {
  ok: boolean;
  issues: TargetIssue[];
  totalPct: number;
  /** Signed distance from 100 - drives the same meter the goal weightage uses. */
  remainingPct: number;
}

/**
 * Every grade must carry a whole-number target and the six must total exactly 100.
 *
 * Integers only, for the same reason goal weightage is: three bands at 33.33 total
 * 99.99 and fail an exact-100 check for a reason no user can see or fix.
 */
export function validateTargets(targets: ReadonlyArray<GradeTarget>): TargetValidation {
  const issues: TargetIssue[] = [];
  const seen = new Set<Grade>();

  for (const target of targets) {
    if (seen.has(target.grade)) {
      issues.push({
        code: 'DUPLICATE_GRADE',
        message: `Grade ${target.grade} is listed twice.`,
        grade: target.grade,
      });
      continue;
    }
    seen.add(target.grade);

    if (!Number.isInteger(target.targetPct)) {
      issues.push({
        code: 'NOT_INTEGER',
        message: `Target for ${target.grade} must be a whole percent.`,
        grade: target.grade,
      });
      continue;
    }
    if (target.targetPct < 0 || target.targetPct > TARGET_TOTAL_PCT) {
      issues.push({
        code: 'OUT_OF_RANGE',
        message: `Target for ${target.grade} must be between 0 and ${TARGET_TOTAL_PCT}.`,
        grade: target.grade,
      });
    }
  }

  for (const grade of GRADES) {
    if (!seen.has(grade)) {
      issues.push({ code: 'MISSING_GRADE', message: `Grade ${grade} has no target.`, grade });
    }
  }

  const totalPct = targets.reduce(
    (sum, t) => sum + (Number.isInteger(t.targetPct) ? t.targetPct : 0),
    0,
  );

  if (totalPct !== TARGET_TOTAL_PCT) {
    const delta = TARGET_TOTAL_PCT - totalPct;
    issues.push({
      code: 'TOTAL_MISMATCH',
      message:
        delta > 0
          ? `Targets total ${totalPct}% - ${delta}% short of ${TARGET_TOTAL_PCT}%.`
          : `Targets total ${totalPct}% - ${-delta}% over ${TARGET_TOTAL_PCT}%.`,
      grade: null,
    });
  }

  return {
    ok: issues.length === 0,
    issues,
    totalPct,
    remainingPct: TARGET_TOTAL_PCT - totalPct,
  };
}

export interface BandVariance {
  grade: Grade;
  targetPct: number;
  /** How many people the target implies for this population. */
  targetCount: number;
  actualCount: number;
  /** actual - target. Positive means over-represented. */
  delta: number;
  /** Actual share, two decimals, for the "10% vs 5%" caption. */
  actualPct: number;
}

/**
 * Target counts for a real population, using largest-remainder apportionment.
 *
 * Rounding each band independently is the trap: 5% of 7 people is 0.35, and six
 * naively-rounded bands add up to 6 or 8 rather than 7. Then the panel shows
 * "1 over target" in a band that is not actually over, and the whole screen stops
 * being trustworthy. Largest remainder guarantees the six counts sum to exactly
 * the population.
 *
 * Ties break toward the higher grade by declaration order, so the result is
 * deterministic rather than dependent on sort stability.
 */
export function bandVariance(
  targets: ReadonlyArray<GradeTarget>,
  counts: Readonly<Partial<Record<Grade, number>>>,
): BandVariance[] {
  const byGrade = new Map(targets.map((t) => [t.grade, t.targetPct]));
  const population = GRADES.reduce((sum, grade) => sum + (counts[grade] ?? 0), 0);

  const exact = GRADES.map((grade, order) => {
    const targetPct = byGrade.get(grade) ?? 0;
    const raw = (targetPct * population) / TARGET_TOTAL_PCT;
    const floor = Math.floor(raw);
    return { grade, order, targetPct, floor, remainder: raw - floor };
  });

  let remaining = population - exact.reduce((sum, e) => sum + e.floor, 0);
  const byRemainder = [...exact].sort(
    (a, b) => b.remainder - a.remainder || a.order - b.order,
  );
  const bonus = new Set<Grade>();
  for (const entry of byRemainder) {
    if (remaining <= 0) break;
    bonus.add(entry.grade);
    remaining -= 1;
  }

  return exact.map(({ grade, targetPct, floor }) => {
    const targetCount = floor + (bonus.has(grade) ? 1 : 0);
    const actualCount = counts[grade] ?? 0;
    return {
      grade,
      targetPct,
      targetCount,
      actualCount,
      delta: actualCount - targetCount,
      actualPct:
        population === 0 ? 0 : Math.round((actualCount / population) * 10000) / 100,
    };
  });
}
